import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Can } from 'core/components/permissions/Can';
import {
  currencyFormatter,
  dateFormatter,
  getFullName,
  isNumeric,
} from 'core/utils/commonUtils';
import { currencyCol } from 'core/utils/muiGridUtils';
import { getOrderPayoutTypeName } from 'core/utils/orderUtils';
import useOrderPayouts, { SWR_ORDER_PAYOUTS_ENDPOINT } from 'hooks/swr/useOrderPayouts';
import useResponsive from 'hooks/useResponsive';
import { isArray, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { OrderPayoutTypeEnum } from 'models/enums/orderPayoutTypeEnum';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import { useContext, useState } from 'react';
import * as orderPayoutService from 'services/orderPayoutService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import { useOrderPageStore } from '../store/useOrderPageStore';
import PayoutEditDialog from './PayoutEditDialog';
import type { GridColDef } from '@mui/x-data-grid';
import type { OrderPayoutInfo } from 'models/order/orderPayoutInfo';
import type { FC } from 'react';
import type { CreateOrUpdateOrderPayoutRequest } from 'services/dtoModels/order/createOrUpdateOrderPayoutRequest';

const OrderPayoutsEdit: FC<OrderPayoutsEditProps> = observer(({ orderId }) => {
  const isSmScreen = useResponsive('down', 'sm');
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [editedPayout, setEditedPayout] = useState<OrderPayoutInfo>();
  const { data: payouts, isLoading } = useOrderPayouts(orderId, isNumeric(orderId ?? ''));

  const { userStore } = useContext(StoreContext);
  const orderPageStore = useOrderPageStore();

  const handlePayoutDialogCancel: VoidFunction = () => {
    setPayoutDialogOpen(false);
  };

  const handlePayoutDialogSave = async (payout: OrderPayoutInfo): Promise<boolean> => {
    const isNew = !isNil(editedPayout) && editedPayout.id <= 0;

    const request: CreateOrUpdateOrderPayoutRequest = {
      datePayout: payout.datePayout,
      remark: payout.remark,
      sum: payout.sum,
      type: payout.type,
      userPayoutId: payout.userPayout?.id,
    };

    try {
      const query = isNew
        ? orderPayoutService.createOrderPayoutAsync(+(orderId ?? 0), request)
        : orderPayoutService.updateOrderPayoutAsync(+(orderId ?? 0), payout.id, request);

      await query;

      await mutate(
        (key) => isArray(key) && key[0] === SWR_ORDER_PAYOUTS_ENDPOINT,
        undefined
      );

      setPayoutDialogOpen(false);
      setEditedPayout(undefined);

      return true;
    } catch {
      return false;
    }
  };

  const handleAddPayout = (): void => {
    setEditedPayout({
      id: -Date.now(),
      dateCreate: new Date(),
      datePayout: new Date(),
      remark: '',
      sum: 0,
      type: OrderPayoutTypeEnum.Cash,
      userCreated: {
        id: userStore.userData?.id ?? 0,
        fullName: !isNil(userStore.userData) ? getFullName(userStore.userData) : '',
      },
      order: {
        id: orderId,
        number: orderPageStore.order?.number ?? '',
        dateCreate: orderPageStore.order?.dateCreate ?? new Date(),
        state: orderPageStore.order?.state ?? OrderStateEnum.Request,
        address: orderPageStore.order?.address ?? '',
        client: {
          id: orderPageStore.order?.client?.id ?? 0,
          fullName: orderPageStore.order?.client?.fullName ?? '',
          phone: orderPageStore.order?.client?.phone ?? '',
        },
      },
    });
    setPayoutDialogOpen(true);
  };

  const handleEdit = (payout: OrderPayoutInfo): void => {
    setEditedPayout(payout);
    setPayoutDialogOpen(true);
  };

  const columns: Array<GridColDef<OrderPayoutInfo>> = [
    {
      field: 'dateCreate',
      headerName: 'Дата',
      // valueFormatter: (value) => dateFormatter.format(value),
      type: 'date',
      minWidth: 120,
    },
    {
      field: 'sum',
      headerName: 'Сумма',
      ...currencyCol,
    },
    {
      field: 'type',
      headerName: 'Тип',
      valueGetter: (value) => getOrderPayoutTypeName(value),
      flex: 0.3,
    },
    {
      field: 'userCreated',
      headerName: 'Создал',
      flex: 0.8,
      valueGetter: (_, row) => row.userCreated?.fullName ?? '',
    },
    {
      field: 'userPayout',
      headerName: 'Получатель',
      flex: 0.8,
      valueGetter: (_, row) => row.userPayout?.fullName ?? '',
    },
    { field: 'remark', headerName: 'Примечание', flex: 1 },
    ...(userStore.ability?.can('update', 'Payouts') ?? false
      ? [
          {
            field: 'actions',
            maxWidth: 40,
            type: 'actions',
            getActions: (params) => [
              <GridActionsCellItem
                icon={<EditIcon />}
                onClick={() => {
                  handleEdit(params.row);
                }}
                label='Edit'
                key={params.row.id}
              />,
            ],
          } satisfies GridColDef<OrderPayoutInfo>,
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <>
        <Skeleton variant='rounded' width={58} height={36} sx={{ marginBottom: 2 }} />
        <Skeleton variant='rounded' height={200} />
      </>
    );
  }

  return (
    <Stack spacing={2}>
      <Can I='create' a='Payouts'>
        <Box>
          <Button variant='outlined' onClick={handleAddPayout}>
            Добавить
          </Button>
        </Box>
      </Can>

      {isSmScreen ? (
        <List disablePadding>
          {payouts?.map((payout) => (
            <ListItem
              key={payout.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge='end'
                  aria-label='edit'
                  onClick={() => {
                    handleEdit(payout);
                  }}>
                  <Can I='update' a='Payouts'>
                    <EditIcon />
                  </Can>
                </IconButton>
              }>
              <ListItemText
                primary={`${currencyFormatter.format(
                  Number(payout.sum)
                )} от ${dateFormatter.format(payout.dateCreate)}`}
                secondary={`${getOrderPayoutTypeName(payout.type)}. ${
                  payout.userPayout?.fullName ? `${payout.userPayout?.fullName}. ` : ''
                }${payout.remark}`}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <DataGrid
          rows={payouts ?? []}
          columns={columns}
          hideFooter
          disableRowSelectionOnClick
          autoHeight
        />
      )}

      {editedPayout && (
        <PayoutEditDialog
          open={payoutDialogOpen}
          payout={editedPayout}
          onCancel={handlePayoutDialogCancel}
          onSave={handlePayoutDialogSave}
        />
      )}
    </Stack>
  );
});

export interface OrderPayoutsEditProps {
  orderId: number;
}

export default OrderPayoutsEdit;
